import type { InventoryChoiceSetWithSelectionResponse } from '@yoop/server-model/inventory-category';
import { DeliveryMethod } from '@yoop/server-model/inventory-category';
import type { TokenChoice } from '@yoop/whitelabel-data';
import {
  FeeReason,
  FeeType,
  FeeValueType,
  TokenDeliveryMethod,
  type TokenFee,
} from '@yoop/whitelabel-data';
import {
  type BreakdownItemResponse,
  BreakdownType,
  Reason,
  ValueType,
} from '@yoop/server-model/fees';
import type { OfferSelectedInventoryChoiceResponse } from '@yoop/server-model/token-info';
import { isNilOrEmpty, isNotNilOrEmpty } from '@yoop/util';
import { log } from '@yoop/service-logger';

export const deliveryMethodFrom = (method: DeliveryMethod) => {
  switch (method) {
    case DeliveryMethod.SHIPPING:
      return TokenDeliveryMethod.Shipping;
    case DeliveryMethod.PICKUP:
      return TokenDeliveryMethod.PickUp;
    default:
      return TokenDeliveryMethod.Digital;
  }
};

// TODO: [Token Choices Refactor] - Simplify these two functions when refactoring TokenChoice/ItemOption
export const tokenChoiceFrom = (
  choiceSet: InventoryChoiceSetWithSelectionResponse,
): TokenChoice | undefined => {
  if (!choiceSet.selectedChoice) {
    // A malformed choice set (missing selected choice) is a backend
    // inconsistency, not an impossible state — skip it rather than throwing
    // and crashing the whole checkout/receipt render. Callers filter undefined.
    log.error(
      `Cannot map choice set "${choiceSet.choiceSetId}" to a token choice: it has no selected choice.`,
    );
    return undefined;
  }
  return {
    name: choiceSet.name,
    selection: choiceSet.selectedChoice.name,
    id: choiceSet.choiceSetId.toString(),
    priceDifference: 0,
  };
};

export const tokenChoicesFrom = (
  selectedChoices: OfferSelectedInventoryChoiceResponse,
  choiceSets: InventoryChoiceSetWithSelectionResponse[],
): TokenChoice[] => {
  if (isNilOrEmpty(choiceSets)) {
    return [];
  }
  return (selectedChoices?.selectedChoiceSetList ?? [])
    .map((selectedChoice): TokenChoice | undefined => {
      const choiceSet = choiceSets.find((set) => set.choiceSetId === selectedChoice.choiceSetId);
      if (!choiceSet) {
        // Backend inconsistency — log and skip this choice instead of throwing.
        log.error(
          `Cannot map selected choice: no choice set found for id "${selectedChoice.choiceSetId}".`,
        );
        return undefined;
      }
      const choice = choiceSet.choices.find((it) => it.id === selectedChoice.choiceId);
      if (!choice) {
        log.error(
          `Cannot map selected choice: no choice found for id "${selectedChoice.choiceId}" in choice set "${selectedChoice.choiceSetId}".`,
        );
        return undefined;
      }

      return {
        name: choiceSet.name,
        id: selectedChoice.choiceSetId.toString(),
        selection: choice.name,
        priceDifference: 0,
      };
    })
    .filter(isNotNilOrEmpty);
};

export const tokenFeeFrom = (feeBreakdown: BreakdownItemResponse): TokenFee | undefined => {
  if (feeBreakdown.breakdownType === BreakdownType.DISCOUNT) {
    const { discountResponse } = feeBreakdown;
    if (!discountResponse) {
      // Backend inconsistency — log and skip this fee line instead of throwing
      // and crashing the entire checkout/receipt page. Callers filter undefined.
      log.error('Cannot map discount breakdown item: it has no discount response.');
      return undefined;
    }
    return {
      id: discountResponse.discountId.toString(),
      type: FeeType.Discount,
      amount: feeBreakdown.amount,
      name: feeBreakdown.name,
    };
  }

  const { feeResponse } = feeBreakdown;
  if (!feeResponse) {
    log.error('Cannot map fee breakdown item: it has no fee response.');
    return undefined;
  }
  return {
    id: feeResponse.feeId.toString(),
    type: FeeType.Regular,
    amount: feeBreakdown.amount,
    name: feeBreakdown.name,
    reason: feeReasonFrom(feeResponse.reason),
    value: feeResponse.value,
    valueType: feeValueTypeFrom(feeResponse.valueType),
  };
};

const feeReasonFrom = (serverReason: Reason | undefined) => {
  switch (serverReason) {
    case Reason.YOOP:
      return FeeReason.Yoop;
    case Reason.PAYMENT:
      return FeeReason.Payment;
    case Reason.CUSTOM:
      return FeeReason.Custom;
    case Reason.TAX:
      return FeeReason.Tax;
    case Reason.PROFIT_SHARE:
      return FeeReason.ProfitShare;
    default:
      return undefined;
  }
};

const feeValueTypeFrom = (serverType: ValueType) => {
  switch (serverType) {
    case ValueType.AMOUNT:
      return FeeValueType.Amount;
    case ValueType.PERCENTAGE:
      return FeeValueType.Percent;
    case ValueType.PROFIT_PERCENTAGE:
      return FeeValueType.ProfitPercent;
    case ValueType.STEPPED:
      return FeeValueType.Stepped;
    case ValueType.TBD:
      return FeeValueType.Pending;
    default:
      return undefined;
  }
};
